// Enhanced Stripe configuration with fallbacks and error handling

// Stripe publishable keys
const STRIPE_TEST_KEY = 'pk_test_51Rni4K081iDmshnQy9ROEG6LIbcF4xU68Oj3qpqgPRmfO5Ynqsgt0rn1bKPKoywqGDYMP6wyt9vLBDw7qtBSnLch00CdqHGbKQ';
const STRIPE_LIVE_KEY = 'pk_live_51Rni4C0T0gJevieX9Euonx8MDxtwkNg7GFbNmDpYUR5WvtEblTatxLCmKw8720JNIzImcKv4NfRpT1WoBAO0StvD00muJ2FDo6';

// Test user email patterns that always use Stripe sandbox mode
// Must match STRIPE_TEST_USER_PATTERNS in backend/blueprints/payments.py
const STRIPE_TEST_USER_PATTERNS = ['tylerhargave', 'kweaston'];

/**
 * Check if an email belongs to a designated Stripe test user.
 * Test users always use Stripe sandbox keys, even in production.
 */
export const isStripeTestUser = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const emailLower = email.toLowerCase();
  return STRIPE_TEST_USER_PATTERNS.some(pattern => emailLower.includes(pattern));
};

/**
 * Get the Stripe publishable key based on environment.
 * Does NOT account for test users - use getStripeKeyForUser() instead
 * when user email is available.
 */
export const getStripeKey = (): string => {
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.includes('localhost');

  if (isLocalhost) {
    return STRIPE_TEST_KEY;
  } else {
    return STRIPE_LIVE_KEY;
  }
};

/**
 * Get the Stripe publishable key for a specific user.
 * Test users always get the test key, even in production.
 */
export const getStripeKeyForUser = (email: string | null | undefined): string => {
  // Test users always get sandbox key
  if (isStripeTestUser(email)) {
    return STRIPE_TEST_KEY;
  }
  // Everyone else gets environment-based key
  return getStripeKey();
};

export const isTestMode = (): boolean => {
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.includes('localhost');
  return isLocalhost;
};

/**
 * Check if a user is in test mode (either localhost OR test user email).
 */
export const isTestModeForUser = (email: string | null | undefined): boolean => {
  return isTestMode() || isStripeTestUser(email);
};

// Alternative Stripe CDN URLs in case of timeout
export const getStripeCDNUrls = () => [
  'https://js.stripe.com/v3/',
  'https://js.stripe.com/basil/stripe.js',
  'https://m.stripe.com/v3/'
];

// Check if we're in a development environment
export const isDevelopment = () => {
  return process.env.NODE_ENV === 'development' ||
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
};

// Get appropriate timeout for Stripe loading
export const getStripeTimeout = () => {
  if (isDevelopment()) {
    return 15000; // Longer timeout for development
  }
  return 10000; // Standard timeout for production
};
